import { useEffect, useState } from "react";
import { Car, Loader2, Plus, Trash2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PhotoGallery } from "@/components/PhotoGallery";
import { Input } from "@/components/ui/input";

const PAY_MODELS = [
  { key: "percentage", label: "Percentage split" },
  { key: "fixed_deposit", label: "Fixed deposit" },
  { key: "full", label: "Full payment at reservation" },
  { key: "after_trip", label: "Pay after trip" },
];

const PAYOUT_MODELS = [
  { key: "none", label: "No payout shown" },
  { key: "percentage", label: "Percentage of the fare" },
  { key: "flat", label: "Flat rate per trip" },
  { key: "per_mile", label: "Per mile" },
  { key: "per_hour", label: "Per hour" },
];

const empty = {
  name: "", make: "", model: "", year: "", color: "", license_plate: "",
  capacity: 4, luggage_capacity: 4, photo_url: "", photos: [], status: "active",
  pricing_mode: "mileage",
  reservation_fee: 30, per_mile_rate: 2.75, minimum_charge: 0, minimum_miles: 0,
  hourly_rate: 100, minimum_hours: 3, included_miles_per_hour: 40, additional_mile_rate: 2.75,
  extra_stop_fee: 15, airport_fee: 0, airport_flat_rate: 0, meet_greet_fee: 0, waiting_rate: 60, overtime_rate: 75, cancellation_fee: 0,
  tax_percent: 0, payment_model: "percentage", deposit_percent: 25, deposit_amount: 50,
  payout_model: "none", payout_percent: 0, payout_flat: 0, payout_per_mile: 0, payout_per_hour: 0,
};

const NUM = new Set([
  "capacity", "luggage_capacity", "reservation_fee", "per_mile_rate", "minimum_charge", "minimum_miles",
  "hourly_rate", "minimum_hours", "included_miles_per_hour", "additional_mile_rate", "extra_stop_fee",
  "airport_fee", "airport_flat_rate", "waiting_rate", "overtime_rate", "cancellation_fee", "tax_percent",
  "meet_greet_fee",
  "deposit_percent", "deposit_amount",
  "payout_percent", "payout_flat", "payout_per_mile", "payout_per_hour",
]);

const Field = ({ label, testId, hint, ...rest }) => (
  <div>
    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-gold">{label}</label>
    <Input data-testid={testId} className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" {...rest} />
    {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
  </div>
);

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/vehicles").then(({ data }) => setItems(data)).catch((e) => setError(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount; it is re-called by hand after each save
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, NUM.has(k) ? Number(v || 0) : v])
    );
    try {
      if (editing) await api.put(`/vehicles/${editing}`, payload);
      else await api.post("/vehicles", payload);
      setForm(empty);
      setEditing(null);
      load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (v) => {
    setEditing(v.id);
    setForm({ ...empty, ...v });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const describePay = (v) => {
    if (v.payment_model === "full") return "100% at reservation";
    if (v.payment_model === "after_trip") return "0% reservation / 100% after trip";
    if (v.payment_model === "fixed_deposit") return `${money(v.deposit_amount)} deposit`;
    return `${v.deposit_percent}% / ${100 - v.deposit_percent}%`;
  };

  const describePayout = (v) => {
    if (v.payout_model === "percentage") return `payout ${v.payout_percent}% of fare`;
    if (v.payout_model === "flat") return `payout ${money(v.payout_flat)}/trip`;
    if (v.payout_model === "per_mile") return `payout ${money(v.payout_per_mile)}/mi`;
    if (v.payout_model === "per_hour") return `payout ${money(v.payout_per_hour)}/h`;
    return "no driver payout set";
  };

  const describeRate = (v) =>
    v.pricing_mode === "hourly"
      ? `${money(v.hourly_rate)}/hour · ${v.minimum_hours}h minimum · ${v.included_miles_per_hour} mi/h included · ${money(v.additional_mile_rate)}/extra mi`
      : `${money(v.reservation_fee)} reservation + ${money(v.per_mile_rate)}/mile${v.minimum_charge ? ` · ${money(v.minimum_charge)} minimum` : ""}`;

  return (
    <div data-testid="vehicles-page" className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Fleet</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Vehicles & pricing</h1>
        <p className="mt-2 text-sm text-slate-400">
          Two pricing engines: point-to-point mileage or hourly charter. Every rate below is yours to change.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-7 rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-100">{editing ? "Edit vehicle" : "Add vehicle"}</h2>
          {editing && (
            <Button type="button" variant="ghost" data-testid="vehicle-cancel-edit" className="text-slate-400"
              onClick={() => { setEditing(null); setForm(empty); }}>
              Cancel edit
            </Button>
          )}
        </div>

        <div>
          <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-slate-500">Vehicle details</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Display name" testId="vehicle-name" value={form.name} onChange={set("name")} required placeholder="2024 Chevrolet Suburban" />
            <Field label="Make" testId="vehicle-make" value={form.make} onChange={set("make")} />
            <Field label="Model" testId="vehicle-model" value={form.model} onChange={set("model")} />
            <Field label="Year" testId="vehicle-year" value={form.year} onChange={set("year")} />
            <Field label="Color" testId="vehicle-color" value={form.color} onChange={set("color")} />
            <Field label="License plate" testId="vehicle-plate" value={form.license_plate} onChange={set("license_plate")} />
            <Field label="Passengers" testId="vehicle-capacity" type="number" value={form.capacity} onChange={set("capacity")} />
            <Field label="Luggage" testId="vehicle-luggage" type="number" value={form.luggage_capacity} onChange={set("luggage_capacity")} />
            <PhotoGallery testId="vehicle-gallery" photos={form.photos || []}
              onChange={(photos) => setForm({ ...form, photos, photo_url: photos[0] || "" })} />
          </div>
        </div>

        <div>
          <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-slate-500">Pricing mode</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              { key: "mileage", title: "Point-to-point / mileage", body: "Reservation fee + per-mile rate, distance from Google Maps." },
              { key: "hourly", title: "Hourly charter", body: "Hourly rate, minimum hours, included miles per hour." },
            ].map((o) => (
              <button
                type="button"
                key={o.key}
                data-testid={`vehicle-mode-${o.key}`}
                onClick={() => setForm({ ...form, pricing_mode: o.key })}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  form.pricing_mode === o.key ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/30"
                }`}
              >
                <p className="text-sm font-medium text-slate-100">{o.title}</p>
                <p className="mt-1 text-xs text-slate-400">{o.body}</p>
              </button>
            ))}
          </div>
        </div>

        {form.pricing_mode === "mileage" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Field label="Reservation fee" testId="vehicle-reservation-fee" type="number" step="0.01" value={form.reservation_fee} onChange={set("reservation_fee")} />
            <Field label="Per-mile rate" testId="vehicle-per-mile" type="number" step="0.01" value={form.per_mile_rate} onChange={set("per_mile_rate")} />
            <Field label="Minimum charge" testId="vehicle-min-charge" type="number" step="0.01" value={form.minimum_charge} onChange={set("minimum_charge")} />
            <Field label="Minimum miles" testId="vehicle-min-miles" type="number" step="0.1" value={form.minimum_miles} onChange={set("minimum_miles")} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Field label="Hourly rate" testId="vehicle-hourly-rate" type="number" step="0.01" value={form.hourly_rate} onChange={set("hourly_rate")} />
            <Field label="Minimum hours" testId="vehicle-min-hours" type="number" step="0.5" value={form.minimum_hours} onChange={set("minimum_hours")} />
            <Field label="Included miles / hour" testId="vehicle-included-miles" type="number" value={form.included_miles_per_hour} onChange={set("included_miles_per_hour")} />
            <Field label="Additional mile rate" testId="vehicle-additional-mile" type="number" step="0.01" value={form.additional_mile_rate} onChange={set("additional_mile_rate")} />
          </div>
        )}

        <div>
          <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-slate-500">Fees & taxes</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <Field label="Extra stop" testId="vehicle-stop-fee" type="number" step="0.01" value={form.extra_stop_fee} onChange={set("extra_stop_fee")} />
            <Field label="Airport fee" testId="vehicle-airport-fee" type="number" step="0.01" value={form.airport_fee} onChange={set("airport_fee")} />
            <Field label="Airport flat rate" hint="Fixed price for any airport run when no zone route matches. 0 = use mileage." testId="vehicle-airport-flat" type="number" step="0.01" value={form.airport_flat_rate} onChange={set("airport_flat_rate")} />
            <Field label="Meet & greet" testId="vehicle-meet-greet-fee" type="number" step="0.01" value={form.meet_greet_fee} onChange={set("meet_greet_fee")} />
            <Field label="Waiting / hour" testId="vehicle-waiting-rate" type="number" step="0.01" value={form.waiting_rate} onChange={set("waiting_rate")} />
            <Field label="Overtime / hour" testId="vehicle-overtime-rate" type="number" step="0.01" value={form.overtime_rate} onChange={set("overtime_rate")} />
            <Field label="Cancellation" testId="vehicle-cancellation-fee" type="number" step="0.01" value={form.cancellation_fee} onChange={set("cancellation_fee")} />
            <Field label="Tax %" testId="vehicle-tax" type="number" step="0.01" value={form.tax_percent} onChange={set("tax_percent")} />
          </div>
        </div>

        <div>
          <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-slate-500">Deposit rule</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-gold">Payment model</label>
              <select
                data-testid="vehicle-payment-model"
                value={form.payment_model}
                onChange={set("payment_model")}
                className="h-11 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
              >
                {PAY_MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            {form.payment_model === "percentage" && (
              <Field label="Reservation %" testId="vehicle-deposit-percent" type="number" value={form.deposit_percent} onChange={set("deposit_percent")} />
            )}
            {form.payment_model === "fixed_deposit" && (
              <Field label="Fixed deposit" testId="vehicle-deposit-amount" type="number" step="0.01" value={form.deposit_amount} onChange={set("deposit_amount")} />
            )}
          </div>
        </div>

        <div>
          <p className="mb-1 font-mono text-[11px] uppercase tracking-widest text-slate-500">Chauffeur payout</p>
          <p className="mb-3 text-xs text-slate-500">
            What the chauffeur earns on this vehicle. Drivers only ever see this figure — never the customer's
            fare, deposit or balance.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-gold">Payout method</label>
              <select
                data-testid="vehicle-payout-model"
                value={form.payout_model}
                onChange={set("payout_model")}
                className="h-11 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
              >
                {PAYOUT_MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            {form.payout_model === "percentage" && (
              <Field label="Payout %" testId="vehicle-payout-percent" type="number" step="0.1" value={form.payout_percent} onChange={set("payout_percent")} hint="Percentage of the fare before tax" />
            )}
            {form.payout_model === "flat" && (
              <Field label="Flat per trip" testId="vehicle-payout-flat" type="number" step="0.01" value={form.payout_flat} onChange={set("payout_flat")} />
            )}
            {form.payout_model === "per_mile" && (
              <Field label="Payout per mile" testId="vehicle-payout-per-mile" type="number" step="0.01" value={form.payout_per_mile} onChange={set("payout_per_mile")} />
            )}
            {form.payout_model === "per_hour" && (
              <Field label="Payout per hour" testId="vehicle-payout-per-hour" type="number" step="0.01" value={form.payout_per_hour} onChange={set("payout_per_hour")} />
            )}
          </div>
        </div>

        {error && <p data-testid="vehicle-error" className="text-sm text-red-400">{error}</p>}
        <Button data-testid="vehicle-save-btn" type="submit" disabled={busy} className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="mr-2 h-4 w-4" /> {editing ? "Save vehicle" : "Add vehicle"}</>)}
        </Button>
      </form>

      <div className="space-y-3">
        {items.length === 0 && (
          <p data-testid="vehicles-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-8 text-center text-sm text-slate-500">
            No vehicles yet.
          </p>
        )}
        {items.map((v) => (
          <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="card-hover flex items-start justify-between gap-4 rounded-xl border border-ink-600 bg-ink-800 p-5">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Car className="h-4 w-4 text-gold" />
                <p className="truncate text-base font-medium text-slate-100">{v.name}</p>
                <span className="rounded-full border border-gold/30 bg-gold/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-gold">
                  {v.pricing_mode}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-slate-500">
                {[v.year, v.make, v.model, v.color, v.license_plate].filter(Boolean).join(" · ")} · {v.capacity} pax · {v.luggage_capacity} bags
              </p>
              <p className="mt-2 font-mono text-xs text-slate-300">{describeRate(v)}</p>
              <p className="mt-1 font-mono text-[11px] text-slate-500">
                Deposit {describePay(v)} · stop {money(v.extra_stop_fee)} · wait {money(v.waiting_rate)}/h · overtime {money(v.overtime_rate)}/h
              </p>
              <p data-testid={`vehicle-payout-summary-${v.id}`} className="mt-1 font-mono text-[11px] text-emerald-300/80">
                {describePayout(v)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <Button data-testid={`vehicle-edit-${v.id}`} size="sm" variant="outline" onClick={() => startEdit(v)}
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                Edit
              </Button>
              <button
                data-testid={`vehicle-delete-${v.id}`}
                onClick={async () => { await api.delete(`/vehicles/${v.id}`); load(); }}
                className="text-slate-500 transition-colors hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
